import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import Form from 'components/common/Form';
import Input from 'components/common/Form/Input';
import Select from 'components/common/Form/Select';
import Button, { ButtonStyles } from 'components/common/Form/Button';

import { MANGO_PAY_COUNTRIES } from 'components/common/listConstants';
import { isModerator, isRespondent, isTranslator } from 'constants/UserRoles';

import style from 'components/Welcome/Form/Form.module.scss';
import InputAPI from 'components/common/Form/InputAPI/InputAPI';
import _, { isUndefined } from 'lodash';
import cn from 'classnames';
import getCountryISO2 from 'country-iso-3-to-2';
import { UserContext } from 'context/UserContext';

export default function AddressForm({
    data,
    onChange,
    onNextStep,
    onPreviousStep,
    isLastStep,
    errors,
    role,
    validate,
    validateForm,
    isModal,
}) {
    const { address, city, state, postalCode, country, house, street } = data;
    const { t } = useTranslation();
    const { user } = useContext(UserContext);
    const displayTooltips = isRespondent(role);

    const isAddressEntered = address && _.isObject(address) && !!Object.keys(address).length;
    useEffect(() => {
        if (!isAddressEntered) return;
        const { houseNumber, street, city, state, postalCode } = address.address;
        const country = MANGO_PAY_COUNTRIES.find((country) => country.value === getCountryISO2(address.countryCode));

        !isUndefined(houseNumber) && onChange('house', houseNumber);
        onChange('street', street);
        onChange('city', city);
        onChange('state', state);
        onChange('postalCode', postalCode);
        country ? onChange('country', country.value) : onChange('country', '');
        validateForm({ street, city, state, postalCode, country, house: houseNumber });
    }, [isAddressEntered]);
    return (
        <div className={style.profileFormContainer}>
            <Form
                className={cn(style.form, {
                    [style.wideForm]: isTranslator(user.role) || isModerator(user.role),
                    [style.modal]: isModal,
                })}
            >
                <div className={style.inlineFieldsContainer}>
                    <InputAPI
                        label={t('welcome:form.labels.enterAddress')}
                        placeholder={t('welcome:form.placeholders.enterAddress')}
                        fullWidth
                        onChange={onChange.bind(this, 'address')}
                        customTooltipText={displayTooltips && t('tooltips:addressLine')}
                        wrapperClassName={style.locationClassName}
                        previewClassName={style.locationPreview}
                    />
                </div>
                {isAddressEntered && (
                    <>
                        <div className={style.inlineFieldsContainer}>
                            <Input
                                label={t('welcome:form.labels.house')}
                                name='house'
                                placeholder={t('welcome:form.placeholders.house')}
                                value={house}
                                onChange={onChange.bind(this, 'house')}
                                error={t(errors.house)}
                                customTooltipText={displayTooltips && t('tooltips:city')}
                                labelClassName={style.labelClassName}
                                onBlur={validate.bind(this, data, 'house')}
                            />
                            <Input
                                label={t('welcome:form.labels.street')}
                                name='street'
                                placeholder={t('welcome:form.placeholders.street')}
                                value={street}
                                onChange={onChange.bind(this, 'street')}
                                error={t(errors.street)}
                                customTooltipText={displayTooltips && t('tooltips:street')}
                                labelClassName={style.labelClassName}
                                onBlur={validate.bind(this, data, 'street')}
                                firstCapitalized
                            />
                        </div>
                        <div className={style.inlineFieldsContainer}>
                            <Input
                                label={t('welcome:form.labels.city')}
                                name='city'
                                placeholder={t('welcome:text.yourCity')}
                                value={city}
                                onChange={onChange.bind(this, 'city')}
                                error={t(errors.city)}
                                customTooltipText={displayTooltips && t('tooltips:city')}
                                labelClassName={style.labelClassName}
                                onBlur={validate.bind(this, data, 'city')}
                                firstCapitalized
                            />
                            <Input
                                label={t('welcome:form.labels.state')}
                                name='state'
                                placeholder={t('welcome:text.stateRegion')}
                                value={state}
                                onChange={onChange.bind(this, 'state')}
                                error={t(errors.state)}
                                customTooltipText={displayTooltips && t('tooltips:state')}
                                labelClassName={style.labelClassName}
                                onBlur={validate.bind(this, data, 'state')}
                                firstCapitalized
                            />
                        </div>
                        <div className={style.inlineFieldsContainer}>
                            <Input
                                label={t('welcome:form.labels.postalCode')}
                                name='postalCode'
                                placeholder={t('welcome:text.postalCode')}
                                value={postalCode}
                                onChange={onChange.bind(this, 'postalCode')}
                                error={t(errors.postalCode)}
                                customTooltipText={displayTooltips && t('tooltips:postalCode')}
                                labelClassName={style.labelClassName}
                                onBlur={validate.bind(this, data, 'postalCode')}
                            />
                            <Select
                                label={t('welcome:form.labels.country')}
                                name='country'
                                placeholder={t('welcome:text.selectCountry')}
                                value={country}
                                options={MANGO_PAY_COUNTRIES}
                                onChange={onChange.bind(this, 'country')}
                                error={t(errors.country)}
                                customTooltipText={displayTooltips && t('tooltips:country')}
                                labelClassName={style.labelClassName}
                                onBlur={validate.bind(this, data, 'country')}
                            />
                        </div>
                        {isLastStep && <div className={style.commonError}>{errors.common}</div>}
                    </>
                )}
                <div className={cn(style.buttonWrapper, style.withBottomMargin)}>
                    <Button
                        text={isLastStep ? t('welcome:buttons.getStarted') : t('welcome:buttons.continue')}
                        onClick={onNextStep}
                        btnStyle={ButtonStyles.primary}
                        disabled={!isAddressEntered}
                    />
                </div>
            </Form>
        </div>
    );
}
